import {
	Button,
	Card,
	Header,
	Message,
	Placeholder,
	Popup,
	Segment,
} from '@mairistem/composants';
import { getErrorMessage, Icon } from '@mairistem/finances-utils';
import { isEmpty } from 'lodash';
import React from 'react';
import { toast } from 'react-toastify';
import DeleteDemandeCompteModal from './DeleteDemandeCompteModal';
import DemandeCompteDetailModal from './DemandeCompteDetailModal';
import DemandeCompteModal from './DemandeCompteModal';
import DemandeDeCompteCertificatModal from './DemandeDeCompteCertificatModal';
import StatutModal from './StatutModal';
import UpdateDemandeCompteModal from './UpdateDemandeCompteModal';
import './DemandeCompteList.css';
import DemandeStatusEnum from '../../enums/demandeStatusEnum';
import type { Role } from '../../interfaces/auth';
import type {
	DemandeCompte,
	DemandeCompteEditableFields,
	JsonStatus,
} from '../../interfaces/demandeCompte';
import {
	deleteDemandeCompte,
	replaceCertificat,
	updateDemandeCompte,
	updateDemandeCompteStatus,
} from '../../services/demandeCompteService';

interface DemandeCompteListProps {
	demandesComptes: DemandeCompte[];
	setDemandesComptes: (demandesComptes: DemandeCompte[]) => void;
	isLoadingDemandesComptes: boolean;
	userRole: Role;
}

const DemandeCompteList = ({
	demandesComptes,
	setDemandesComptes,
	isLoadingDemandesComptes,
	userRole,
}: DemandeCompteListProps) => {
	const [idDemandeStatutModalOpen, setIdDemandeStatutModalOpen] =
		React.useState<number | null>(null);
	const [idDeleteDemandeCompteModalOpen, setIdDeleteDemandeCompteModalOpen] =
		React.useState<number | null>(null);
	const [
		certificatDemandeCompteModalOpen,
		setCertificatDemandeCompteModalOpen,
	] = React.useState<DemandeCompte | null>(null);
	const [isStatutModalLoading, setIsStatutModalLoading] =
		React.useState<boolean>(false);
	const [isDeleteDemandeCompteModalLoading, setIsDeleteDemandeCompteLoading] =
		React.useState<boolean>(false);
	const [isUpdateCertificatModalLoading, setIsUpdateCertificatModalLoading] =
		React.useState<boolean>(false);
	const [demandeCompteDetailModalOpen, setDemandeCompteDetailModalOpen] =
		React.useState<DemandeCompte | null>(null);
	const [updateDemandeCompteModalOpen, setUpdateDemandeCompteModalOpen] =
		React.useState<DemandeCompte | null>(null);
	const [
		isUpdateDemandeCompteModalLoading,
		setIsUpdateDemandeCompteModalLoading,
	] = React.useState<boolean>(false);

	const handleUpdateDemandeCompte = async (
		updatedFields: DemandeCompteEditableFields,
	) => {
		try {
			if (!updateDemandeCompteModalOpen) return;
			setIsUpdateDemandeCompteModalLoading(true);
			await updateDemandeCompte({
				...updateDemandeCompteModalOpen,
				...updatedFields,
			});

			const updatedDemandesComptes = demandesComptes.map((dc) => {
				if (dc.identifiant === updateDemandeCompteModalOpen.identifiant) {
					return { ...dc, ...updatedFields };
				}
				return dc;
			});
			setDemandesComptes(updatedDemandesComptes);
			toast.success('Demande de compte mise à jour avec succès');
			setUpdateDemandeCompteModalOpen(null);
		} catch (error) {
			toast.error(
				getErrorMessage(
					error,
					'Erreur lors de la mise à jour de la demande de compte',
				),
			);
		} finally {
			setIsUpdateDemandeCompteModalLoading(false);
		}
	};

	const handleUpdateStatut = async (
		statut: string,
		messagePrive: string,
		messagePublic: string,
	) => {
		try {
			if (!idDemandeStatutModalOpen) return;
			const result: { status: { status: string } } =
				await updateDemandeCompteStatus(
					idDemandeStatutModalOpen,
					statut,
					messagePrive,
					messagePublic,
				);

			const isRazStatus = statut === 'RazStatus';
			const updatedDemandesComptes = demandesComptes.map((demandeCompte) => {
				if (demandeCompte.identifiant === idDemandeStatutModalOpen) {
					if (isRazStatus) {
						return { ...demandeCompte, status: '', statusCode: '' };
					}
					return { ...demandeCompte, status: JSON.stringify(result.status) };
				}
				return demandeCompte;
			});
			setDemandesComptes(updatedDemandesComptes);
			toast.success('Statut de la demande de compte mis à jour avec succès');
			setIdDemandeStatutModalOpen(null);
		} catch (error) {
			toast.error(
				getErrorMessage(
					error,
					'Erreur lors de la mise à jour du statut de la demande de compte',
				),
			);
		} finally {
			setIsStatutModalLoading(false);
		}
	};

	const handleDeleteDemandeCompte = async (id: number) => {
		try {
			setIsDeleteDemandeCompteLoading(true);
			await deleteDemandeCompte(id);

			const updatedDemandesComptes = demandesComptes.filter(
				(demandeCompte) => demandeCompte.identifiant !== id,
			);
			setDemandesComptes(updatedDemandesComptes);
			toast.success('Demande de compte supprimée avec succès');
			setIdDeleteDemandeCompteModalOpen(null);
		} catch (error) {
			toast.error(
				getErrorMessage(
					error,
					'Erreur lors de la suppression de la demande de compte',
				),
			);
		} finally {
			setIsDeleteDemandeCompteLoading(false);
		}
	};

	const handleUpdateCertificat = async (
		idDemandeCompte: number,
		certificat: File,
	) => {
		try {
			setIsUpdateCertificatModalLoading(true);
			const result: {
				status: string;
				path: string;
				idDemandeCompte: string;
			} = await replaceCertificat(idDemandeCompte, certificat);

			const updatedDemandesComptes = demandesComptes.map((demandeCompte) => {
				if (demandeCompte.identifiant === idDemandeCompte) {
					return { ...demandeCompte, certificat: result.path };
				}
				return demandeCompte;
			});
			setDemandesComptes(updatedDemandesComptes);
			toast.success(
				'Certificat de la demande de compte mis à jour avec succès',
			);
			setCertificatDemandeCompteModalOpen(null);
		} catch (error) {
			toast.error(
				getErrorMessage(
					error,
					'Erreur lors de la mise à jour du certificat de la demande de compte',
				),
			);
		} finally {
			setIsUpdateCertificatModalLoading(false);
		}
	};

	return (
		<Segment className="demande-compte-list">
			<Header textAlign="left" className="infos-list-header">
				Nouveaux utilisateurs créés depuis Mes informations Client
				{userRole === 'admin' && (
					<DemandeCompteModal
						setDemandesComptes={setDemandesComptes}
						demandesComptes={demandesComptes}
					/>
				)}
			</Header>

			<div className="list-content">
				{isLoadingDemandesComptes ? (
					<Placeholder>
						<Placeholder.Header>
							<Placeholder.Line />
							<Placeholder.Line />
						</Placeholder.Header>
						<Placeholder.Paragraph>
							<Placeholder.Line />
							<Placeholder.Line />
							<Placeholder.Line />
							<Placeholder.Line />
						</Placeholder.Paragraph>
					</Placeholder>
				) : (
					<>
						{demandesComptes?.length === 0 && (
							<Message info>
								Aucune demande de création de compte en attente
							</Message>
						)}
						{demandesComptes?.map((demandeCompte) => {
							let status: string;
							let jsonStatus: JsonStatus | null = null;
							if (!isEmpty(demandeCompte.status)) {
								try {
									jsonStatus = JSON.parse(demandeCompte.status ?? '');
								} catch {
									jsonStatus = null;
								}
							}

							if (isEmpty(demandeCompte.statusCode)) {
								status = 'Non traité';
							} else if (
								demandeCompte.statusCode === DemandeStatusEnum.OK ||
								demandeCompte.statusCode === DemandeStatusEnum.TRAITEE
							) {
								const dateTraitement = demandeCompte.dateTraitement
									? new Date(demandeCompte.dateTraitement)
									: null;
								const dateFormatted =
									dateTraitement && !Number.isNaN(dateTraitement.getTime())
										? dateTraitement.toLocaleDateString('fr-FR')
										: '';
								status = dateFormatted
									? `${demandeCompte.statusCode} le ${dateFormatted}`
									: `${demandeCompte.statusCode}`;
								if (userRole === 'admin' && jsonStatus?.userCim) {
									status += ` par ${jsonStatus.userCim}`;
								}
							} else {
								status = demandeCompte.statusCode ?? '';
							}

							const certificatExpireDate = demandeCompte.certificatExpire
								? new Date(demandeCompte.certificatExpire)
								: null;
							const isCertificatExpired =
								demandeCompte.certificat &&
								certificatExpireDate &&
								!Number.isNaN(certificatExpireDate.getTime())
									? certificatExpireDate.getTime() <= Date.now()
									: false;

							return (
								<Card key={demandeCompte.identifiant} fluid>
									<Card.Content className="demande-content">
										<div className="demande-user-info">
											{userRole === 'admin' && (
												<Popup
													content={`Identifiant: ${demandeCompte.identifiant}`}
													trigger={
														<span
															data-testid={`demande-id-${demandeCompte.identifiant}`}
														>
															#{demandeCompte.identifiant}
														</span>
													}
												/>
											)}
											<span
												className="demande-user-name"
												data-testid={`demande-user-${demandeCompte.identifiant}`}
											>
												{demandeCompte.prenom} {demandeCompte.nom}
											</span>
											{demandeCompte.qualite && (
												<>
													<span className="demande-separator">|</span>
													<span className="demande-user-qualite">
														{demandeCompte.qualite}
													</span>
												</>
											)}
											<span className="demande-separator">|</span>
											<span className="demande-user-email">
												{demandeCompte.email}
											</span>
										</div>

										<div className="demande-code-client">
											<span className="demande-code-client-label">
												Code client
											</span>
											<span className="demande-code-client-value">
												{demandeCompte.codeClient}
											</span>
										</div>

										<div className="demande-cert-status">
											{demandeCompte.certificat && !isCertificatExpired && (
												<Popup
													content={`Expire le ${certificatExpireDate?.toLocaleDateString('fr-FR') ?? ''}`}
													trigger={
														<span className="demande-cert-status">
															<Icon name="check" size={14} color="green" />
															<span className="checked-certificat">Valide</span>
														</span>
													}
												/>
											)}
											{demandeCompte.certificat && isCertificatExpired && (
												<Popup
													content={`A expiré le ${certificatExpireDate?.toLocaleDateString('fr-FR') ?? ''}`}
													trigger={
														<span className="demande-cert-status">
															<Icon
																iconSet="Lucide"
																name="X"
																size={14}
																color="red"
															/>
															<span className="not-checked-certificat">
																Expiré
															</span>
														</span>
													}
												/>
											)}
											{!demandeCompte.certificat && (
												<>
													<Icon iconSet="Lucide" name="X" size={14} />
													<span>Aucun cert.</span>
												</>
											)}
										</div>

										<div className="demande-statut">
											<Icon iconSet="Lucide" name="BookmarkCheck" size={14} />
											{status}
										</div>

										<div className="demande-actions">
											{userRole === 'admin' && (
												<>
													<Popup
														content="Voir le détail"
														trigger={
															<Button
																onClick={() =>
																	setDemandeCompteDetailModalOpen(demandeCompte)
																}
																size="mini"
																icon
															>
																<Icon name="Eye" iconSet="Lucide" size={14} />
															</Button>
														}
													/>
													<Popup
														content="Supprimer la demande"
														trigger={
															<Button
																onClick={() =>
																	setIdDeleteDemandeCompteModalOpen(
																		demandeCompte.identifiant,
																	)
																}
																size="mini"
																icon
															>
																<Icon
																	name="Trash2"
																	iconSet="Lucide"
																	color="red"
																	size={14}
																/>
															</Button>
														}
													/>
													<Popup
														content="Changer le certificat"
														trigger={
															<Button
																onClick={() =>
																	setCertificatDemandeCompteModalOpen(
																		demandeCompte,
																	)
																}
																size="mini"
																icon
															>
																<Icon name="File" iconSet="Lucide" size={14} />
															</Button>
														}
													/>
													<Popup
														content="Mettre à jour le statut"
														trigger={
															<Button
																onClick={() =>
																	setIdDemandeStatutModalOpen(
																		demandeCompte.identifiant,
																	)
																}
																size="mini"
																icon
															>
																<Icon
																	name="RefreshCw"
																	iconSet="Lucide"
																	size={14}
																/>
															</Button>
														}
													/>
													<Popup
														content="Modifier la demande"
														trigger={
															<Button
																onClick={() =>
																	setUpdateDemandeCompteModalOpen(demandeCompte)
																}
																size="mini"
																icon
															>
																<Icon
																	name="Pencil"
																	iconSet="Lucide"
																	size={14}
																/>
															</Button>
														}
													/>
												</>
											)}
										</div>
									</Card.Content>
								</Card>
							);
						})}
					</>
				)}
			</div>
			{idDemandeStatutModalOpen && (
				<StatutModal
					onClose={() => setIdDemandeStatutModalOpen(null)}
					onValidate={handleUpdateStatut}
					isLoading={isStatutModalLoading}
				/>
			)}
			{idDeleteDemandeCompteModalOpen && (
				<DeleteDemandeCompteModal
					onClose={() => setIdDeleteDemandeCompteModalOpen(null)}
					onValidate={() =>
						handleDeleteDemandeCompte(idDeleteDemandeCompteModalOpen)
					}
					isLoading={isDeleteDemandeCompteModalLoading}
				/>
			)}
			{certificatDemandeCompteModalOpen && (
				<DemandeDeCompteCertificatModal
					demandeCompte={certificatDemandeCompteModalOpen}
					onClose={() => setCertificatDemandeCompteModalOpen(null)}
					onChangeCertificat={handleUpdateCertificat}
					isLoading={isUpdateCertificatModalLoading}
				/>
			)}
			{demandeCompteDetailModalOpen && (
				<DemandeCompteDetailModal
					demandeCompte={demandeCompteDetailModalOpen}
					onClose={() => setDemandeCompteDetailModalOpen(null)}
				/>
			)}
			{updateDemandeCompteModalOpen && (
				<UpdateDemandeCompteModal
					demandeCompte={updateDemandeCompteModalOpen}
					onClose={() => setUpdateDemandeCompteModalOpen(null)}
					onValidate={handleUpdateDemandeCompte}
					isLoading={isUpdateDemandeCompteModalLoading}
				/>
			)}
		</Segment>
	);
};

export default DemandeCompteList;
